// Utility functions for handling image downloads, conversions, and caching

// In-memory cache for generated images
const imageCache = new Map();

/**
 * Get a cached image based on prompt
 * @param {string} prompt - The image generation prompt
 * @returns {string|null} The cached image base64 string or null if not found
 */
export const getCachedImage = (prompt) => {
  // Normalize the prompt to ensure consistent cache hits
  const normalizedPrompt = prompt.trim().toLowerCase();
  return imageCache.get(normalizedPrompt) || null;
};

/**
 * Store an image in the cache
 * @param {string} prompt - The image generation prompt
 * @param {string} imageBase64 - The base64 string of the image
 */
export const cacheImage = (prompt, imageBase64) => {
  // Normalize the prompt to ensure consistent cache hits
  const normalizedPrompt = prompt.trim().toLowerCase();
  
  // Add to cache (limit cache size to prevent memory issues)
  if (imageCache.size > 100) {
    // If cache gets too big, remove the oldest entry
    const oldestKey = imageCache.keys().next().value;
    imageCache.delete(oldestKey);
  }
  
  imageCache.set(normalizedPrompt, imageBase64);
  console.log(`[imageUtils] Cached image for prompt: ${normalizedPrompt.substring(0, 30)}...`);
};

export const downloadImage = async (imageUrl, isGenerated = false, promptText = '') => {
  try {
    let blob;
    let mimeType = 'image/png'; // Default mime type

    if (imageUrl.startsWith('data:')) {
      // Handle base64 images
      const matches = imageUrl.match(/^data:([^;]+);base64,/);
      if (matches) {
        mimeType = matches[1];
      }
      const base64Data = imageUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays.push(byteCharacters.charCodeAt(i));
      }

      blob = new Blob([new Uint8Array(byteArrays)], { type: mimeType });
    } else {
      // Check if it's an Azure Blob Storage URL
      const isAzureBlob = imageUrl.includes('blob.core.windows.net');

      if (isAzureBlob) {
        // Handle Azure Blob Storage URLs with XMLHttpRequest
        blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.onload = () => {
            if (xhr.status === 200) {
              const contentType = xhr.getResponseHeader('content-type');
              mimeType = contentType || mimeType;
              resolve(new Blob([xhr.response], { type: mimeType }));
            } else {
              reject(new Error(`Azure Blob download failed! status: ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Network error during Azure Blob download'));
          xhr.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              console.log(`Download progress: ${percentComplete.toFixed(2)}%`);
            }
          };
          xhr.open('GET', imageUrl);
          xhr.responseType = 'blob';
          xhr.setRequestHeader('Accept', 'image/*');
          xhr.send();
        });
      } else {
        // Handle other URLs with fetch and fallbacks
        try {
          const response = await fetch(imageUrl, {
            headers: { 'Accept': 'image/*' },
            mode: isGenerated ? 'cors' : 'no-cors',
            credentials: 'omit'
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          blob = await response.blob();
          mimeType = blob.type || mimeType;
        } catch (fetchError) {
          // Fallback to proxy for non-Azure URLs
          const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(imageUrl)}`;
          const proxyResponse = await fetch(proxyUrl, { mode: 'cors' });
          if (!proxyResponse.ok) {
            throw new Error(`Proxy fetch failed! status: ${proxyResponse.status}`);
          }
          blob = await proxyResponse.blob();
          mimeType = blob.type || mimeType;
        }
      }
    }

    // Get file extension from mime type
    const extension = mimeType.split('/')[1] || 'png';

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Create a kid-friendly filename using the prompt text if available
    let filename;
    if (promptText && promptText.trim() !== '') {
      // Clean up the prompt to make it suitable for a filename
      // - Take only the first few words
      // - Remove special characters that aren't suitable for filenames
      const cleanPrompt = promptText.trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 30);
      
      filename = `${cleanPrompt}.${extension}`;
    } else {
      // Fallback to a simple generic name if no prompt is provided
      filename = `scratch-drawing.${extension}`;
    }

    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 200);

    return true;
  } catch (error) {
    console.error('Error downloading image:', error);
    throw new Error(`Failed to download image: ${error.message}`);
  }
};

export const handleImagePaste = async (event) => {
  try {
    // Check if the pasted content is a URL
    const text = event.clipboardData?.getData('text');
    if (text && (text.startsWith('http://') || text.startsWith('https://'))) {
      try {
        const response = await fetch(text);
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.startsWith('image/')) {
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              if (typeof e.target.result === 'string' &&
                  e.target.result.startsWith('data:image/')) {
                resolve(e.target.result);
              } else {
                reject(new Error('Invalid image data'));
              }
            };
            reader.onerror = () => reject(new Error('Failed to read image URL'));
            reader.readAsDataURL(blob);
          });
        }
      } catch (error) {
        console.error('Error fetching image URL:', error);
        throw new Error('Invalid image URL or unable to load image');
      }
    }

    // Handle clipboard paste
    if (event.clipboardData?.items) {
      const items = event.clipboardData.items;
      for (const item of items) {
        if (item.type.indexOf('image') === 0) {
          const file = item.getAsFile();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              // Validate base64 data
              if (typeof e.target.result === 'string' &&
                  e.target.result.startsWith('data:image/')) {
                resolve(e.target.result);
              } else {
                reject(new Error('Invalid image data'));
              }
            };
            reader.onerror = () => reject(new Error('Failed to read image file'));
            reader.readAsDataURL(file);
          });
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error handling image paste:', error);
    throw new Error('Failed to process pasted image');
  }
};
